import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../../admin/components/shared/Tooltip.tsx';
import { CheckboxInput } from '../../../components/form/inputs/CheckboxInput.tsx';
import { draftRowCellTdClass, DraftFieldCell } from '../../../shared/draftReviewTableLayout.tsx';

const inputBase =
  'box-border w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500';
const inputErr =
  'border-red-500 bg-red-50/50 focus:border-red-500 focus:ring-red-500/30 focus:ring-2';
const SINGLE_CONTROL = 'min-h-[2.375rem]';
const DESC_CONTROL = 'min-h-[4.75rem]';
const viewWrapOk = 'box-border w-full rounded-lg border border-transparent px-3 py-2 text-sm';
const viewWrapErr =
  'box-border w-full rounded-lg border border-red-500 bg-red-50/30 px-3 py-2 text-sm shadow-sm shadow-red-900/5';
const DEFAULT_PAGE_SIZES = [5, 10, 25, 50];

export type DraftReviewRowBase = { id: string };

export type DraftTextColumn<F extends string> = {
  type: 'text';
  field: F;
  header: string;
  shellClassName: string;
  multiline?: boolean;
  emptyLabel: string;
};

export type DraftSelectColumn<F extends string> = {
  type: 'select';
  field: F;
  header: string;
  shellClassName: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  normalizeValue?: (value: unknown) => string;
  selectPlaceholder?: string;
};

export type DraftErrorsColumn<F extends string> = {
  type: 'errors';
  field: F;
  header: string;
  shellClassName: string;
  detailTitle?: string;
};

export type DraftColumn<F extends string> =
  | DraftTextColumn<F>
  | DraftSelectColumn<F>
  | DraftErrorsColumn<F>;

export type DraftReviewTableProps<TRow extends DraftReviewRowBase, F extends string> = {
  rows: TRow[];
  columns: DraftColumn<F>[];
  selectedIds: Set<string>;
  onToggleAll?: (checked: boolean) => void;
  onToggleRow: (rowId: string, checked: boolean) => void;
  selectAllAriaLabel?: string;
  rowSelectAriaLabel: (row: TRow) => string;
  getFieldValue: (row: TRow, field: F) => unknown;
  getFieldReasons: (row: TRow, field: F) => string[];
  rowHasError: (row: TRow) => boolean;
  /** When set, only these errors disable row selection (defaults to rowHasError). */
  rowBlocksSelection?: (row: TRow) => boolean;
  isFieldEditing: (rowId: string, field: string) => boolean;
  startEditing: (rowId: string, field: string) => void;
  stopEditing: () => void;
  updateField: (rowId: string, field: string, value: string) => void;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  /** Tighter row/cell padding for bulk-upload review tables. */
  compact?: boolean;
};

function firstValidationMessage(reasons: string[]): string | null {
  return reasons.length > 0 ? reasons[0] : null;
}

function CellTooltip({
  content,
  children,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
}): React.ReactElement {
  return <Tooltip content={content}>{children}</Tooltip>;
}

function parseValidationReason(reason: string): { field: string; message: string } {
  const idx = reason.indexOf(': ');
  if (idx === -1) return { field: '', message: reason };
  return { field: reason.slice(0, idx), message: reason.slice(idx + 2) };
}

type DraftTableDensity = {
  singleControl: string;
  descControl: string;
  inputBase: string;
  viewWrapOk: string;
  viewWrapErr: string;
  hintMargin: string;
  thPad: string;
};

function getDraftTableDensity(compact: boolean): DraftTableDensity {
  if (!compact) {
    return {
      singleControl: SINGLE_CONTROL,
      descControl: DESC_CONTROL,
      inputBase,
      viewWrapOk,
      viewWrapErr,
      hintMargin: 'mt-1',
      thPad: 'px-3 py-2.5',
    };
  }
  return {
    singleControl: 'min-h-[2rem]',
    descControl: 'min-h-[3rem]',
    inputBase:
      'box-border w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-900 outline-none transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500',
    viewWrapOk: 'box-border w-full rounded-lg border border-transparent px-2 py-1 text-sm',
    viewWrapErr:
      'box-border w-full rounded-lg border border-red-500 bg-red-50/30 px-2 py-1 text-sm shadow-sm shadow-red-900/5',
    hintMargin: 'mt-0.5',
    thPad: 'px-2 py-2',
  };
}

function DraftErrorsCellContent({
  reasons,
  shellClassName,
  detailTitle,
  density,
}: {
  reasons: string[];
  shellClassName: string;
  detailTitle: string;
  density: DraftTableDensity;
}): React.ReactElement {
  const [detailOpen, setDetailOpen] = useState(false);
  const invalid = reasons.length > 0;

  useEffect(() => {
    if (!detailOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDetailOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [detailOpen]);

  return (
    <>
      <DraftFieldCell shellClassName={shellClassName} hint={null}>
        <div
          className={`${density.singleControl} flex items-center text-sm ${
            invalid ? density.viewWrapErr : `${density.viewWrapOk} hover:bg-gray-100`
          }`}
        >
          {invalid ? (
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="inline-flex w-fit items-center rounded-md border border-red-200 bg-white px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50"
            >
              View details
            </button>
          ) : (
            <span className="text-xs font-medium text-green-700">Verified</span>
          )}
        </div>
      </DraftFieldCell>
      {detailOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <button
                type="button"
                className="absolute inset-0 bg-black/40"
                aria-label="Close validation details"
                onClick={() => setDetailOpen(false)}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="draft-errors-detail-title"
                className="relative z-10 flex max-h-[min(32rem,85vh)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
              >
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <h3 id="draft-errors-detail-title" className="text-sm font-semibold text-gray-900">
                    {detailTitle}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDetailOpen(false)}
                    className="rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <ul className="list-none space-y-3 overflow-y-auto px-4 py-4">
                  {reasons.map((reason) => {
                    const { field, message } = parseValidationReason(reason);
                    return (
                      <li key={reason} className="break-words text-sm leading-snug text-gray-700">
                        {field ? (
                          <>
                            <span className="font-medium text-gray-900">{field}:</span> {message}
                          </>
                        ) : (
                          message
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setDetailOpen(false)}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function DraftReviewTable<TRow extends DraftReviewRowBase, F extends string>({
  rows,
  columns,
  selectedIds,
  onToggleAll,
  onToggleRow,
  selectAllAriaLabel,
  rowSelectAriaLabel,
  getFieldValue,
  getFieldReasons,
  rowHasError,
  rowBlocksSelection,
  isFieldEditing,
  startEditing,
  stopEditing,
  updateField,
  initialPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  compact = false,
}: DraftReviewTableProps<TRow, F>): React.ReactElement {
  const density = useMemo(() => getDraftTableDensity(compact), [compact]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() =>
    pageSizeOptions.includes(initialPageSize) ? initialPageSize : pageSizeOptions[0] ?? 10
  );
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const showSelectAllHeader = typeof onToggleAll === 'function';

  useEffect(() => {
    setPageIndex(0);
  }, [rows.length]);

  const blocksSelection = rowBlocksSelection ?? rowHasError;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
  const safePage = Math.min(pageIndex, totalPages - 1);
  const sliceStart = safePage * pageSize;

  useEffect(() => {
    if (pageIndex !== safePage) setPageIndex(safePage);
  }, [pageIndex, safePage]);

  const pageRows = useMemo(
    () => rows.slice(sliceStart, sliceStart + pageSize),
    [rows, sliceStart, pageSize]
  );

  const selectableRows = useMemo(
    () => (showSelectAllHeader ? rows.filter((row) => !blocksSelection(row)) : []),
    [rows, blocksSelection, showSelectAllHeader]
  );

  const allSelected =
    showSelectAllHeader &&
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedIds.has(r.id));
  const someSelected =
    showSelectAllHeader &&
    selectableRows.length > 0 &&
    selectableRows.some((r) => selectedIds.has(r.id)) &&
    !allSelected;
  const selectAllDisabled = showSelectAllHeader && selectableRows.length === 0;

  useEffect(() => {
    if (!showSelectAllHeader) return;
    const el = headerCheckboxRef.current;
    if (el) el.indeterminate = someSelected;
  }, [someSelected, allSelected, rows.length, showSelectAllHeader]);

  const fromRow = rows.length === 0 ? 0 : sliceStart + 1;
  const toRow = Math.min(sliceStart + pageSize, rows.length);

  const renderTextCell = (row: TRow, col: DraftTextColumn<F>) => {
    const reasons = getFieldReasons(row, col.field);
    const hint = firstValidationMessage(reasons);
    const invalid = reasons.length > 0;
    const str = String(getFieldValue(row, col.field) ?? '');
    const tip = str || col.emptyLabel;
    const rowHasAny = rowHasError(row);
    const controlClass = col.multiline
      ? density.descControl
      : `${density.singleControl} flex items-center`;

    return (
      <DraftFieldCell
        key={col.field}
        shellClassName={col.shellClassName}
        hint={hint}
        hintMarginClass={density.hintMargin}
      >
        {isFieldEditing(row.id, col.field) ? (
          <CellTooltip content={tip}>
            {col.multiline ? (
              <textarea
                rows={2}
                value={str}
                onChange={(e) => updateField(row.id, col.field, e.target.value)}
                onBlur={stopEditing}
                autoFocus
                className={`${density.inputBase} ${density.descControl} resize-none ${invalid ? inputErr : ''}`}
                aria-invalid={invalid}
              />
            ) : (
              <input
                value={str}
                onChange={(e) => updateField(row.id, col.field, e.target.value)}
                onBlur={stopEditing}
                autoFocus
                className={`${density.inputBase} ${density.singleControl} ${invalid ? inputErr : ''}`}
                aria-invalid={invalid}
              />
            )}
          </CellTooltip>
        ) : (
          <CellTooltip content={tip}>
            <div
              onClick={() => startEditing(row.id, col.field)}
              className={`${controlClass} line-clamp-2 cursor-pointer text-sm text-gray-700 ${
                invalid ? density.viewWrapErr : `${density.viewWrapOk} hover:bg-gray-100`
              } ${rowHasAny && col.multiline ? density.descControl : ''}`}
            >
              {str ? str : <span className="text-gray-400">{col.emptyLabel}</span>}
            </div>
          </CellTooltip>
        )}
      </DraftFieldCell>
    );
  };

  const renderSelectCell = (row: TRow, col: DraftSelectColumn<F>) => {
    const reasons = getFieldReasons(row, col.field);
    const hint = firstValidationMessage(reasons);
    const invalid = reasons.length > 0;
    const sel = col.normalizeValue
      ? col.normalizeValue(getFieldValue(row, col.field))
      : String(getFieldValue(row, col.field) ?? '');

    return (
      <DraftFieldCell
        key={col.field}
        shellClassName={col.shellClassName}
        hint={hint}
        hintMarginClass={density.hintMargin}
      >
        <select
          value={sel}
          onChange={(e) => updateField(row.id, col.field, e.target.value)}
          className={`${density.inputBase} ${density.singleControl} ${invalid ? inputErr : ''}`}
          aria-invalid={invalid}
          aria-label={col.header}
        >
          <option value="">{col.selectPlaceholder ?? 'Select...'}</option>
          {col.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </DraftFieldCell>
    );
  };

  const renderErrorsCell = (row: TRow, col: DraftErrorsColumn<F>) => {
    const reasons = getFieldReasons(row, col.field);
    return (
      <DraftErrorsCellContent
        key={col.field}
        reasons={reasons}
        shellClassName={col.shellClassName}
        detailTitle={col.detailTitle ?? col.header}
        density={density}
      />
    );
  };

  return (
    <TooltipProvider>
      <div className="bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`${density.thPad} align-middle text-xs font-medium uppercase tracking-wider text-gray-500 ${
                    showSelectAllHeader ? 'text-center' : 'w-10'
                  }`}
                >
                  {showSelectAllHeader ? (
                    <CellTooltip content={selectAllAriaLabel ?? ''}>
                      <span className="inline-flex">
                        <CheckboxInput
                          ref={headerCheckboxRef}
                          checked={allSelected}
                          disabled={selectAllDisabled}
                          onChange={(e) => {
                            if (selectAllDisabled) return;
                            onToggleAll?.(e.target.checked);
                          }}
                          aria-label={selectAllAriaLabel}
                        />
                      </span>
                    </CellTooltip>
                  ) : (
                    <span className="sr-only">Select row</span>
                  )}
                </th>
                {columns.map((col) => (
                  <th
                    key={col.field}
                    className={`${col.shellClassName} ${density.thPad} text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pageRows.map((row) => {
                const rowHasAny = rowHasError(row);
                const rowBlocksSelect = blocksSelection(row);
                const isSelected = selectedIds.has(row.id);
                const rowBgClass =
                  isSelected && !rowBlocksSelect
                    ? 'bg-blue-100/60 text-gray-900 hover:bg-blue-100/80'
                    : 'bg-gray-100/90 text-gray-600';
                const rowTdClass = draftRowCellTdClass(rowHasAny, compact);
                const rowSelectLabel = rowSelectAriaLabel(row);
                const checkboxTip = columns
                  .map((c) => String(getFieldValue(row, c.field) ?? '').trim())
                  .filter(Boolean)
                  .join(' — ') || rowSelectLabel;

                return (
                  <tr key={row.id} className={rowBgClass}>
                    <td className={rowTdClass}>
                      <div className="flex items-center justify-center">
                        <CellTooltip
                          content={
                            rowBlocksSelect
                              ? 'Fix validation errors on this row before you can select it.'
                              : checkboxTip
                          }
                        >
                          <span className="inline-flex">
                            <CheckboxInput
                              checked={selectedIds.has(row.id)}
                              disabled={rowBlocksSelect}
                              onChange={(e) => {
                                if (rowBlocksSelect) return;
                                onToggleRow(row.id, e.target.checked);
                              }}
                              aria-label={rowSelectLabel}
                            />
                          </span>
                        </CellTooltip>
                      </div>
                    </td>
                    {columns.map((col) => (
                      <td key={col.field} className={rowTdClass}>
                        {col.type === 'text'
                          ? renderTextCell(row, col)
                          : col.type === 'select'
                            ? renderSelectCell(row, col)
                            : renderErrorsCell(row, col)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-600">
            Showing{' '}
            <span className="font-medium text-gray-800">
              {fromRow}–{toRow}
            </span>{' '}
            of <span className="font-medium text-gray-800">{rows.length}</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <span className="whitespace-nowrap">Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 shadow-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={safePage <= 0}
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[5.5rem] text-center text-xs text-gray-600">
                Page {safePage + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default DraftReviewTable;
